import { Client } from '@microsoft/microsoft-graph-client';
import { formatInTimeZone } from 'date-fns-tz';
import { authService } from '../auth/authService';
import {
  GetScheduleRequest,
  GetScheduleResponse,
  ScheduleInformation,
  User,
  Person,
} from './types';

class GraphService {
  private client: Client | null = null;

  private getClient(): Client {
    if (!this.client) {
      this.client = Client.init({
        authProvider: async (done) => {
          try {
            const token = await authService.getAccessToken();
            done(null, token);
          } catch (error) {
            done(error as Error, null);
          }
        },
      });
    }
    return this.client;
  }

  /**
   * Search for users in the directory
   * @param searchTerm - The search query
   * @returns Array of matching users
   */
  async searchUsers(searchTerm: string): Promise<Person[]> {
    if (!searchTerm || searchTerm.length < 2) {
      return [];
    }

    try {
      const client = this.getClient();

      // Search users using $search query parameter
      // Filter by displayName or mail
      const response = await client
        .api('/users')
        .filter(
          `startsWith(displayName,'${searchTerm}') or startsWith(mail,'${searchTerm}') or startsWith(userPrincipalName,'${searchTerm}')`
        )
        .select('id,displayName,mail,userPrincipalName')
        .top(10)
        .get();

      const users: User[] = response.value || [];

      return users.map((user) => ({
        id: user.id,
        displayName: user.displayName || 'Unknown',
        emailAddress: user.mail || user.userPrincipalName,
        userPrincipalName: user.userPrincipalName,
      }));
    } catch (error) {
      console.error('Error searching users:', error);

      // Return mock data for development if Graph is not available
      if (process.env.NODE_ENV === 'development') {
        return this.getMockUsers(searchTerm);
      }

      throw new Error('Failed to search users. Please check your permissions.');
    }
  }

  /**
   * Get schedule (free/busy) information for multiple users
   * @param emailAddresses - Array of email addresses
   * @param startTime - Start of the time window
   * @param endTime - End of the time window
   * @param timezone - Timezone for the query
   * @param intervalMinutes - Interval in minutes for availability view (default 30)
   * @returns Schedule information for all users
   */
  async getSchedule(
    emailAddresses: string[],
    startTime: Date,
    endTime: Date,
    timezone: string,
    intervalMinutes: number = 30
  ): Promise<ScheduleInformation[]> {
    if (emailAddresses.length === 0) {
      return [];
    }

    try {
      const client = this.getClient();

      // Convert IANA timezone to Windows timezone format for Graph API
      const ianaToWindows: Record<string, string> = {
        'America/New_York': 'Eastern Standard Time',
        'America/Chicago': 'Central Standard Time',
        'America/Denver': 'Mountain Standard Time',
        'America/Los_Angeles': 'Pacific Standard Time',
        'Europe/London': 'GMT Standard Time',
        'Europe/Paris': 'W. Europe Standard Time',
        'Asia/Tokyo': 'Tokyo Standard Time',
        'Australia/Sydney': 'AUS Eastern Standard Time',
        'UTC': 'UTC',
      };

      const windowsTimezone = ianaToWindows[timezone] || 'UTC';

      // Format dates for Graph API
      // IMPORTANT: We want to query from midnight of the given date in the TARGET timezone,
      // not whatever time the date happens to represent.
      // The date parameter represents "which day" we want, so we extract year/month/day
      // and construct a midnight time string for that date.
      const formatDateForGraph = (date: Date, useEndOfDay: boolean = false): string => {
        // Get the date components in the target timezone
        const year = formatInTimeZone(date, timezone, 'yyyy');
        const month = formatInTimeZone(date, timezone, 'MM');
        const day = formatInTimeZone(date, timezone, 'dd');

        // Return midnight (00:00:00) or end of day (23:59:59) for that date
        if (useEndOfDay) {
          return `${year}-${month}-${day}T23:59:59`;
        }
        return `${year}-${month}-${day}T00:00:00`;
      };

      const requestBody: GetScheduleRequest = {
        schedules: emailAddresses,
        startTime: {
          dateTime: formatDateForGraph(startTime, false), // midnight of start date
          timeZone: windowsTimezone,
        },
        endTime: {
          dateTime: formatDateForGraph(endTime, true), // end of day of end date
          timeZone: windowsTimezone,
        },
        availabilityViewInterval: intervalMinutes,
      };

      // Debug: Log request details
      console.log('=== Graph API getSchedule Request ===');
      console.log('Request body:', JSON.stringify(requestBody, null, 2));
      console.log('Input startTime (JS Date):', startTime.toISOString());
      console.log('Input endTime (JS Date):', endTime.toISOString());

      const response: GetScheduleResponse = await client
        .api('/me/calendar/getSchedule')
        .header('Prefer', `outlook.timezone="${timezone}"`)
        .post(requestBody);

      // Debug: Log response
      console.log('=== Graph API getSchedule Response ===');
      console.log('Response value count:', response.value?.length ?? 0);
      response.value?.forEach((schedule, idx) => {
        console.log(`Schedule ${idx + 1} (${schedule.scheduleId}):`);
        console.log(`  availabilityView: "${schedule.availabilityView}"`);
        console.log(`  scheduleItems count: ${schedule.scheduleItems?.length ?? 0}`);
      });

      return response.value || [];
    } catch (error) {
      console.error('Error getting schedule:', error);

      // Return mock data for development
      if (process.env.NODE_ENV === 'development') {
        return this.getMockSchedule(emailAddresses, startTime, endTime, intervalMinutes);
      }

      throw new Error('Failed to retrieve schedules. Please check your permissions.');
    }
  }

  /**
   * Get the current user's mailbox timezone
   * @returns Timezone string (e.g., "Eastern Standard Time")
   */
  async getMailboxTimezone(): Promise<string> {
    try {
      const client = this.getClient();
      const settings = await client.api('/me/mailboxSettings').get();
      return settings.timeZone || 'UTC';
    } catch (error) {
      console.error('Error getting mailbox timezone:', error);
      return 'UTC';
    }
  }

  /**
   * Check which users' calendars are accessible
   * @param emailAddresses - Array of email addresses to check
   * @returns Array of email addresses that are accessible
   */
  async checkCalendarAccess(emailAddresses: string[]): Promise<string[]> {
    if (emailAddresses.length === 0) {
      return [];
    }

    try {
      const client = this.getClient();

      // Use a short time window (just today) to minimize data transfer
      const now = new Date();
      const startTime = new Date(now);
      startTime.setHours(0, 0, 0, 0);
      const endTime = new Date(now);
      endTime.setHours(23, 59, 59, 999);

      const requestBody = {
        schedules: emailAddresses,
        startTime: {
          dateTime: startTime.toISOString().split('T')[0] + 'T00:00:00',
          timeZone: 'UTC',
        },
        endTime: {
          dateTime: endTime.toISOString().split('T')[0] + 'T23:59:59',
          timeZone: 'UTC',
        },
        availabilityViewInterval: 60, // Use larger interval to minimize response size
      };

      const response = await client
        .api('/me/calendar/getSchedule')
        .post(requestBody);

      // Filter to only include schedules that have valid availabilityView
      // If a user's calendar is inaccessible, availabilityView will be empty or error will be set
      const accessibleEmails: string[] = [];

      for (const schedule of response.value || []) {
        // Check if we got valid availability data (not an error)
        if (schedule.availabilityView && schedule.availabilityView.length > 0 && !schedule.error) {
          accessibleEmails.push(schedule.scheduleId);
        }
      }

      return accessibleEmails;
    } catch (error) {
      console.error('Error checking calendar access:', error);
      // On error, return all emails (let the main search handle access issues)
      return emailAddresses;
    }
  }

  // Mock data for development/testing
  private getMockUsers(searchTerm: string): Person[] {
    const mockUsers = [
      { id: '1', displayName: 'Alice Johnson', emailAddress: 'alice@contoso.com' },
      { id: '2', displayName: 'Bob Smith', emailAddress: 'bob@contoso.com' },
      { id: '3', displayName: 'Carol Williams', emailAddress: 'carol@contoso.com' },
      { id: '4', displayName: 'David Brown', emailAddress: 'david@contoso.com' },
      { id: '5', displayName: 'Emma Davis', emailAddress: 'emma@contoso.com' },
    ];

    return mockUsers.filter(
      (user) =>
        user.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.emailAddress.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  private getMockSchedule(
    emailAddresses: string[],
    startTime: Date,
    endTime: Date,
    intervalMinutes: number
  ): ScheduleInformation[] {
    // Generate mock availability view
    // 0 = Free, 1 = Tentative, 2 = Busy, 3 = OOF, 4 = Working elsewhere
    const totalMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60);
    const slots = Math.floor(totalMinutes / intervalMinutes);

    return emailAddresses.map((email) => {
      // Randomize availability for mock data
      let availabilityView = '';
      for (let i = 0; i < slots; i++) {
        const random = Math.random();
        if (random < 0.6) availabilityView += '0'; // 60% free
        else if (random < 0.9) availabilityView += '2'; // 30% busy
        else availabilityView += '1'; // 10% tentative
      }

      return {
        scheduleId: email,
        availabilityView,
        scheduleItems: [],
      };
    });
  }
}

export const graphService = new GraphService();
